import { useEffect, useRef, useState } from 'react';

import Spinner from '@/components/atom/spinner';
import { EDoctorFilterType, getLoadingMessageByFilterType } from '@/models/diagnosis/doctor-filter';
import theme from '@/styles/theme';

interface Props {
  categoryName: string;
  selectedFilters?: EDoctorFilterType[];
}

const SLIDE_INTERVAL_MS = 1000;

const TelemedicineDoctorListLoading = ({ categoryName, selectedFilters = [] }: Props) => {
  const displayCategoryName = categoryName || '증상/과목';
  const hasFilters = selectedFilters.length > 0;
  const [currentFilterIndex, setCurrentFilterIndex] = useState(0);
  const prevFiltersRef = useRef<string>('');

  const currentFiltersKey = JSON.stringify(selectedFilters);

  useEffect(() => {
    if (prevFiltersRef.current !== currentFiltersKey) {
      setCurrentFilterIndex(0);
      prevFiltersRef.current = currentFiltersKey;
    }
  }, [currentFiltersKey]);

  useEffect(() => {
    if (!hasFilters || selectedFilters.length <= 1) {
      setCurrentFilterIndex(0);
      return;
    }

    const intervalId = setInterval(() => {
      setCurrentFilterIndex((prev) => {
        const nextIndex = (prev + 1) % selectedFilters.length;
        return nextIndex;
      });
    }, SLIDE_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [hasFilters, currentFiltersKey, selectedFilters.length]);

  const getLoadingMessage = () => {
    if (!hasFilters) {
      return `${displayCategoryName}을 전문으로 하는 의사 찾는 중...`;
    }

    const currentFilter = selectedFilters[currentFilterIndex];
    return getLoadingMessageByFilterType(currentFilter, displayCategoryName);
  };

  return (
    <div className='box-border flex min-h-[100vh] flex-col items-center justify-center gap-[40px] px-[20px] pt-[40px] pb-[80px]'>
      <Spinner size={48} color={theme.color.primary600} />
      <div className='flex flex-col items-center gap-[12px] text-center'>
        <p className='typography-ui-19sb text-gray-900'>나에게 적합한 의사를 찾고 있어요</p>
        <div className='relative h-[22px] w-full overflow-hidden'>
          <div
            className='typography-ui-15r flex flex-col text-gray-500 transition-transform duration-500 ease-in-out'
            style={{
              transform:
                hasFilters && selectedFilters.length > 1
                  ? `translateY(-${currentFilterIndex * 22}px)`
                  : 'translateY(0)',
            }}
          >
            {hasFilters && selectedFilters.length > 1 ? (
              selectedFilters.map((filter, index) => (
                <p key={`${filter}-${index}`} className='h-[22px] flex-shrink-0 leading-[22px]'>
                  {getLoadingMessageByFilterType(filter, displayCategoryName)}
                </p>
              ))
            ) : (
              <p className='h-[22px] flex-shrink-0 leading-[22px]'>{getLoadingMessage()}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TelemedicineDoctorListLoading;
